"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.safeSchemaParse = safeSchemaParse;
exports.schemaMatches = schemaMatches;
exports.removeNullFields = removeNullFields;
exports.stringifyToolCallArgs = stringifyToolCallArgs;
exports.stringifyToolResultContent = stringifyToolResultContent;
exports.isRichToolResult = isRichToolResult;
exports.parseMetadata = parseMetadata;
exports.getNestedProperty = getNestedProperty;
exports.normalizeToolDefinitionForChatMl = normalizeToolDefinitionForChatMl;
exports.normalizeToolDefinitionsForChatMl = normalizeToolDefinitionsForChatMl;
exports.attachToolDefinitionsToMessages = attachToolDefinitionsToMessages;
const json_1 = require("../json");
/**
 * Zod 4 `safeParse` constructs a `$ZodError` on failure and assigns
 * `inst.name`. That assignment throws when `Error.name` is non-writable
 * (SES/lockdown and some browser extensions). Treat a throw as a failed parse.
 *
 * Catch returns `{ success: false }` only — do not invent a `ZodError` here;
 * constructing one can hit the same non-writable `Error.name` path under SES.
 */
function safeSchemaParse(schema, data) {
    try {
        return schema.safeParse(data);
    }
    catch {
        return { success: false };
    }
}
function schemaMatches(schema, data) {
    return safeSchemaParse(schema, data).success;
}
function removeNullFields(obj) {
    if (!obj || typeof obj !== "object")
        return {};
    const cleaned = { ...obj };
    Object.keys(cleaned).forEach((key) => {
        if (cleaned[key] === null) {
            delete cleaned[key];
        }
    });
    return cleaned;
}
function stringifyToolCallArgs(toolCall) {
    if (!toolCall?.function)
        return toolCall;
    const func = toolCall.function;
    return {
        ...toolCall,
        function: {
            ...func,
            arguments: typeof func.arguments === "string"
                ? func.arguments
                : JSON.stringify(func.arguments ?? {}),
        },
    };
}
function stringifyToolResultContent(content) {
    if (typeof content === "string")
        return content;
    if (content === null || content === undefined)
        return "";
    return JSON.stringify(content);
}
/**
 * used to check if a tool call is "complex" to render as PrettyJsonView table
 * or stringify it and render it as pure string. only used for role: tool type
 * calls. used for tool calls themselves, not for tool selections by an LLM.
 *
 * Rich = has nested structure OR has more than 2 top-level keys
 * Simple <= 2 keys with only scalar values (strings, numbers, booleans, null)
 */
function isRichToolResult(content) {
    if (!content || typeof content !== "object" || Array.isArray(content)) {
        return false;
    }
    const keys = Object.keys(content);
    // More than 2 keys → probably rich/structured data
    if (keys.length > 2)
        return true;
    // Check if any value is an object or array (nested structure)
    for (const key of keys) {
        const value = content[key];
        if (value && typeof value === "object") {
            return true; // Has nested structure
        }
    }
    // 1-2 keys with only scalar values: simple
    return false;
}
function parseMetadata(metadata) {
    if (!metadata)
        return null;
    if (typeof metadata === "string") {
        try {
            return JSON.parse(metadata);
        }
        catch {
            return null;
        }
    }
    if (typeof metadata === "object") {
        return metadata;
    }
    return null;
}
function getNestedProperty(obj, ...path) {
    let current = obj;
    for (const key of path) {
        if (!current || typeof current !== "object")
            return undefined;
        current = current[key];
    }
    return current;
}
function parseArrayIfString(value) {
    if (Array.isArray(value))
        return value;
    const parsed = (0, json_1.parseJsonIfString)(value);
    return Array.isArray(parsed) ? parsed : undefined;
}
function isPlainRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
/**
 * Normalize available tool definitions from provider-specific shapes to ChatML.
 * Built-in provider tools often have no `name`, so we fall back to `id`/`type`
 * for display while backend column extraction can still ignore unnamed tools.
 */
function normalizeToolDefinitionForChatMl(tool) {
    const parsedTool = (0, json_1.parseJsonIfString)(tool);
    if (!isPlainRecord(parsedTool))
        return null;
    const nestedFunction = isPlainRecord(parsedTool.function)
        ? parsedTool.function
        : undefined;
    const source = nestedFunction ?? parsedTool;
    const rawName = source.name ??
        parsedTool.name ??
        parsedTool.id ??
        (parsedTool.type !== "function" ? parsedTool.type : undefined);
    if (typeof rawName !== "string" || rawName.length === 0)
        return null;
    const rawDescription = source.description ?? parsedTool.description;
    const rawParameters = source.parameters ??
        source.parameters_json_schema ??
        source.inputSchema ??
        parsedTool.parameters ??
        parsedTool.parameters_json_schema ??
        parsedTool.inputSchema;
    const normalized = {
        name: rawName,
        description: typeof rawDescription === "string" ? rawDescription : "",
    };
    if (isPlainRecord(rawParameters)) {
        normalized.parameters = rawParameters;
    }
    return normalized;
}
function normalizeToolDefinitionsForChatMl(tools) {
    const parsedTools = parseArrayIfString(tools);
    if (!parsedTools)
        return [];
    return parsedTools
        .map(normalizeToolDefinitionForChatMl)
        .filter((tool) => tool !== null);
}
function dedupeToolDefinitionsForChatMl(tools) {
    const seenNames = new Set();
    return tools.filter((tool) => {
        const name = tool.name;
        if (typeof name !== "string" || name.length === 0)
            return false;
        if (seenNames.has(name))
            return false;
        seenNames.add(name);
        return true;
    });
}
function attachToolDefinitionsToMessages(messages, tools) {
    const dedupedTools = dedupeToolDefinitionsForChatMl(tools);
    if (dedupedTools.length === 0)
        return messages;
    return messages.map((msg) => ({
        ...(isPlainRecord(msg) ? msg : {}),
        tools: dedupedTools,
    }));
}
